import { Injectable } from '@angular/core';

import isEqual from 'lodash/isEqual';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  concatMap,
  filter,
  from,
  map,
  Observable,
  of,
  Subscription,
  take,
  tap,
} from 'rxjs';
import moment from 'moment';

import { CheckoutStatus } from '@shared/stripe/enums';
import { PartyTypes } from '@shared/party/enums';
import { AuthUser } from '@shared/signup/models';
import { Schedule } from '@shared/schedule/models';
import { Resource } from '@shared/resource/models';
import { Location } from '@shared/location/models';
import { getCancellationPolicyWindowInMilliseconds } from '@shared/location/functions';
import { IParty, DateTimeService, FloorplanResources } from '@tablesready/common-ui';
import { ConfirmationCheckboxModalService } from '@tablesready/frontend-confirmation-checkbox-modal';
import { AddEditPartyComponent } from '../reservations/components';
import { ScheduleService } from './schedule.service';
import { IPartyOptions, MarkPartyService } from './mark-party.service';
import { ResourcesService } from './resources.service';
import { AddEditPartyFloorplanComponent } from '../reservations/components/add-edit-party-floorplan';
import { FrontendModalService, FrontendModalSize } from '@tablesready/frontend-modal';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { ChatPartyComponent } from '../components';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import {
  AddEditPartyReactiveComponent
} from '../reservations/components/add-edit-party-reactive/add-edit-party-reactive.component';
type ModalInstance = AddEditPartyFloorplanComponent | AddEditPartyComponent;

@Injectable({ providedIn: 'root' })
export class ReservationService {
  schedule: Schedule[] = [];

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly dateTimeService: DateTimeService,
    private readonly scheduleService: ScheduleService,
    private readonly alertsService: AlertsService,
    private readonly markPartyService: MarkPartyService,
    private readonly confirmationModalService: ConfirmationCheckboxModalService,
    private readonly resourcesService: ResourcesService,
    private readonly authRestaurantService: AuthRestaurantService,
  ) {
    this.subscriptions.add(this.scheduleService.selectAll()
      .pipe(
        tap((schedule) => {
          this.schedule = schedule;
        }),
        catchError(() => {
          this.alertsService.addError('Please, configure business hours');

          return of(null);
        }),
      )
      .subscribe());
  }

  confirmDelete(party: IParty): Observable<unknown> {
    const { location } = this.authRestaurantService.get();
    const label = location.displayOptions.labels.reservation;
    const cancellationTime = getCancellationPolicyWindowInMilliseconds(location.bookingOptions.cancellationPolicy);

    let content = `Are you sure you want to delete ${label}?`;
    const isInsideNoCancellationWindow = Date.now() >= party.quoted_time - cancellationTime;
    if (party.stripeData?.status === CheckoutStatus.paid) {
      if (isInsideNoCancellationWindow) {
        content = `This ${label} made a payment of $${party.stripeData?.totalAmount} that will not be refunded because it is too close to their ${label}. Are you sure you want to delete this ${label}?`;
      } else {
        content = `This ${label} will be canceled and fully refunded. It may take a few days for the money to reach the customer’s bank account. Are you sure you wish to proceed?`;
      }
    }

    return this.confirmationModalService.confirm({
      content,
      closeBtnText: 'Cancel',
      confirmBtnText: 'Delete',
      showCheckBox: party.stripeData?.status === CheckoutStatus.paid && isInsideNoCancellationWindow,
      checkboxContent: `Refund the $${party.stripeData?.totalAmount} payment. It may take a few days for the money to reach the customer’s bank account.`,
      confirmButtonActiveOnCheckboxSelected: false,
    })
      .pipe(
        take(1),
        filter(({ result }) => result?.isConfirmed),
        concatMap(({ result }) => {
          const options = {
            party_id: party._id,
            canceled_time: Date.now(),
            performRefund: result.checkboxStatus,
          };

          return this.markPartyService.setParty(options).pipe(catchError((err) => {
            this.alertsService.add({
              type: IAlertTypes.danger,
              heading: 'Error:',
              body: 'party not deleted successfully.',
            });

            return of(err);
          }));
        }),
        catchError(err => of(err)),
      );
  }

  // ADD & EDIT RESERVATION
  addEditReservation(
    user: AuthUser,
    selectedResource: Resource,
    selectedTimeForReservation: number,
    selectedEndTimeForReservation: number,
    reservationDateFilter: NgbDateStruct | string,
    floorPlanMode = false,
    floorplanResources?: FloorplanResources[],
    showSelectResourceMessage = false,
    showSuccessUpdateAlert = true,
    overrideWithResourceId?: string,
    party?: IParty,
  ): Observable<IParty | null> {
    const partyNoteElement = 'note';
    const settingsNoteElement = 'noteOptions';

    if (
      party?.[partyNoteElement] &&
      typeof party[partyNoteElement] === 'string' &&
      Array.isArray(user[settingsNoteElement]) &&
      user[settingsNoteElement].length > 0) {
      const partyNoteEl = party[partyNoteElement];
      party[partyNoteElement] = partyNoteEl.split(',');
    }

    return this.modalController.create<AddEditPartyFloorplanComponent, IParty>(
      floorPlanMode ? AddEditPartyFloorplanComponent : AddEditPartyComponent,
      {},
      FrontendModalSize.default,
      false,
      (modalElement: HTMLIonModalElement) => {
        const componentProps: Partial<Record<keyof AddEditPartyFloorplanComponent, unknown>> = {};

        componentProps.parties = [];
        componentProps.user = {
          ...user.location,
          account: user.account,
        };
        componentProps.partyToEdit = party;
        componentProps.selectedResource = selectedResource;
        componentProps.userSchedule = this.schedule;
        componentProps.typeOfParty = PartyTypes.reservation;
        componentProps.selectedTime = selectedTimeForReservation;
        componentProps.selectedEndTime = selectedEndTimeForReservation;
        componentProps.showSelectResourceMessage = showSelectResourceMessage;
        componentProps.selectedDate = typeof reservationDateFilter === 'string'
          ? this.dateTimeService.momentToNgbDate(moment.tz(user.location.timezone))
          : reservationDateFilter;

        if (floorplanResources) {
          componentProps.floorplanResources = floorplanResources;
        }

        if (overrideWithResourceId) {
          modalElement.cssClass = 'transparent-ion-modal';
          componentProps.afterInit = (instance: ModalInstance): void => {
            this.afterInitLogic(instance, modalElement, party, overrideWithResourceId, selectedResource, showSuccessUpdateAlert);
          };
        }

        modalElement.componentProps = componentProps;
      },
    )
      .pipe(
        take(1),
        map((updatedParty: IParty) => {
          if (!updatedParty) {
            return updatedParty;
          }

          return {
            ...party,
            ...updatedParty,
          };
        }),
        catchError(err => of(err)),
      );
  }

  addEditReservationReactive(
    user: AuthUser,
    selectedResource: Resource,
    selectedTimeForReservation: number,
    selectedEndTimeForReservation: number,
    reservationDateFilter: NgbDateStruct | string,
    showSelectResourceMessage = false,
    overrideWithResourceId?: string,
    party?: IParty,
  ): Observable<IParty | null> {
    const partyNoteElement = 'note';
    const settingsNoteElement = 'noteOptions';

    if (
      party?.[partyNoteElement] &&
      typeof party[partyNoteElement] === 'string' &&
      Array.isArray(user[settingsNoteElement]) &&
      user[settingsNoteElement].length > 0) {
      const partyNoteEl = party[partyNoteElement];
      party[partyNoteElement] = partyNoteEl.split(',');
    }

    return this.modalController.create<AddEditPartyReactiveComponent, IParty>(
      AddEditPartyReactiveComponent,
      {},
      FrontendModalSize.default,
      false,
      (modalElement: HTMLIonModalElement) => {
        const componentProps: Partial<Record<keyof AddEditPartyReactiveComponent, unknown>> = {};

        componentProps.parties = [];
        componentProps.user = {
          ...user.location,
          account: user.account,
        };
        componentProps.partyToEdit = party;
        componentProps.selectedResource = selectedResource;
        componentProps.userSchedule = this.schedule;
        componentProps.selectedTime = selectedTimeForReservation;
        componentProps.selectedEndTime = selectedEndTimeForReservation;
        componentProps.showSelectResourceMessage = showSelectResourceMessage;
        componentProps.selectedDate = typeof reservationDateFilter === 'string'
          ? this.dateTimeService.momentToNgbDate(moment.tz(user.location.timezone))
          : reservationDateFilter;

        if (overrideWithResourceId) {
          modalElement.cssClass = 'transparent-ion-modal';
          componentProps.afterInit = (instance: ModalInstance): void => {
            this.afterInitLogic(instance, modalElement, party, overrideWithResourceId, selectedResource);
          };
        }

        modalElement.componentProps = componentProps;
      },
    )
      .pipe(
        take(1),
        map((updatedParty: IParty) => {
          if (!updatedParty) {
            return updatedParty;
          }

          return {
            ...party,
            ...updatedParty,
          };
        }),
        catchError(err => of(err)),
      );
  }

  chatParty(party: IParty, user: AuthUser): void {
    from(this.modalController.create<ChatPartyComponent, unknown>(ChatPartyComponent, {
      location: user.location,
      party,
      showTemplates: user.account.features.savedReplies,
    }))
      .pipe(take(1))
      .subscribe();
  }

  checkInAndServe(party: IParty, loc: Location): Observable<unknown> {
    this.alertsService.clear();

    const now = Date.now();
    return this.markPartyService.setParty({
      party_id: party._id,
      checkin_time: now,
      seated_time: now,
    })
      .pipe(
        tap(() => {
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Serving:',
            body: `${loc.displayOptions?.labels?.customer || 'customer'} of ${party.size}`,
          });
        }),
        catchError((err) => {
          this.alertsService.addError(`${loc.displayOptions?.labels?.customer || 'customer'} check-in and serve failure.`);

          return of(err);
        }),
      );
  }

  checkIn(party: IParty, loc: Location): Observable<unknown> {
    this.alertsService.clear();

    return this.markPartyService.setParty({
      party_id: party._id,
      checkin_time: Date.now(),
    })
      .pipe(
        tap(() => {
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Checked In:',
            body: party.carrier
              ? `${party.truck} ${party.carrier}`
              : `${party.name} ${loc.displayOptions?.labels?.customer || 'customer'} of ${party.size}`,
          });
        }),
        catchError((err) => {
          this.alertsService.addError(`${loc.displayOptions?.labels?.customer || 'customer'} check-in failure.`);

          return of(err);
        }),
      );
  }

  completeParty(party: IParty, loc: Location, checkTracking = true): Observable<unknown> {
    const partyCantBeComplete = !party.checkin_time || !party.seated_time;
    const serverTrackingDisabled = checkTracking && !loc.bookingOptions.servingTracking;

    if (partyCantBeComplete || serverTrackingDisabled) {
      return of(null);
    }

    this.alertsService.clear();

    const options: IPartyOptions = {
      party_id: party._id,
      duration: Date.now() - party.seated_time,
      completedAt: Date.now(),
    };

    return this.markPartyService.setParty(options)
      .pipe(
        tap(() => {
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Completed:',
            body: `${party.name} ${loc.displayOptions?.labels?.customer || 'customer'} of ${party.size}`,
          });
        }),
        catchError((err) => {
          this.alertsService.addError(`${loc.displayOptions?.labels?.customer || 'customer'} Served: failure.`);

          return of(err);
        }),
      );
  }

  private afterInitLogic(
    instance: ModalInstance,
    modalElement: HTMLIonModalElement,
    party: IParty,
    overrideWithResourceId: string,
    selectedResource: Resource,
    showSuccessUpdateAlert = true,
  ): void {
    instance.selectResources();
    instance.sizeChanged();
    instance.checkWorkTime();

    const resource = this.resourcesService.getById(overrideWithResourceId);
    if (!resource) {
      this.showModal(instance, modalElement);
      return;
    }

    const differentServices = party?.services?.length > 0 && !isEqual(resource.services.sort(), party.services.sort());
    if (differentServices) {
      instance.clearServicesResources();
    }

    instance.addResourceToSelectedOnes(overrideWithResourceId, resource.services);
    instance.resourceOverrideCallback = (newParty: IParty): void => {
      const extraConflicts = {
        amountOfServicesChanged: party.services.length > 0 && party.services.length !== newParty.services.length,
        wrongResourceSize: party.size < selectedResource.capacity.min || party.size > selectedResource.capacity.max,
        overlapping: instance.showOverlappingAlert,
        wrongPartySize: instance.showResourcesSizeAlert,
      };

      if (differentServices) {
        instance.selectServices();
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.overlapping) {
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.wrongResourceSize) {
        instance.showSizeAlert(selectedResource.capacity);
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.wrongPartySize) {
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.amountOfServicesChanged) {
        instance.sizeChanged();
        instance.selectResources();
        this.showModal(instance, modalElement);
        return;
      }

      const conflicts = instance.doesEditingHaveAnyConflicts();
      let noConflicts = true;
      Object.entries(conflicts).forEach(([, state]) => {
        if (state) {
          noConflicts = false;
        }
      });

      if (!noConflicts) {
        this.showModal(instance, modalElement);
        return;
      }

      if (!newParty?.duration) {
        instance.selectServices();
        instance.resourceOverrideCallback = (): void => {
          this.closeModal(instance, showSuccessUpdateAlert);
        };
      } else {
        this.closeModal(instance);
      }
    };
  }

  private readonly closeModal = (instance: ModalInstance, showSuccessUpdateAlert = true): void => {
    instance.ok(showSuccessUpdateAlert);
  };

  private readonly showModal = (instance: ModalInstance, modal: HTMLIonModalElement): void => {
    instance.resourceOverrideCallback = undefined;
    modal.cssClass = '';
  };
}
